
export interface VerbExercise {
  verb: string;
  infinitive: string;
  subject: string;
  tense: string;
  correctAnswer: string;
  options: string[];
  explanation: string;
  difficulty: 'easy' | 'medium' | 'hard';
  context: string;
}

export interface VerbAnalysis {
  verbs: Array<{
    verb: string;
    infinitive: string;
    tense: string;
    subject: string;
    context: string;
    difficulty: 'easy' | 'medium' | 'hard';
  }>;
  totalVerbs: number;
  difficulty: 'easy' | 'medium' | 'hard';
}

/**
 * Analyze song lyrics to extract verbs and their conjugations using OpenAI
 */
export const analyzeSongVerbs = async (lyrics: string, language: string = 'spanish'): Promise<VerbAnalysis> => {
  try {
    const response = await fetch('/api/analyze-song-verbs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lyrics, language }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Verb analysis failed');
    }

    const analysis = await response.json();
    
    // Validate the response structure
    if (!analysis.verbs || !Array.isArray(analysis.verbs)) {
      throw new Error('Invalid verb analysis format');
    }

    return analysis;
  } catch (error: any) {
    console.error('Error analyzing song verbs:', error);
    
    if (error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to analyze song verbs');
    }
  }
};

/**
 * Generate verb conjugation exercises using OpenAI
 */
export const generateVerbExercises = async (
  verbAnalysis: VerbAnalysis, 
  numberOfExercises: number = 8,
  language: string = 'spanish'
): Promise<VerbExercise[]> => {
  try {
    const response = await fetch('/api/generate-verb-exercises', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ verbAnalysis, numberOfExercises, language }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Exercise generation failed');
    }

    const result = await response.json();
    
    // Handle both array and object responses
    const exercises = result.exercises || [];
    
    if (!Array.isArray(exercises) || exercises.length === 0) {
      throw new Error('Invalid exercise format received');
    }

    return exercises;
  } catch (error: any) {
    console.error('Error generating verb exercises:', error);
    
    if (error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to generate verb exercises');
    }
  }
};

/**
 * Generate additional conjugation options for a verb
 */
export const generateConjugationOptions = async (
  infinitive: string,
  correctAnswer: string,
  tense: string,
  language: string = 'spanish'
): Promise<string[]> => {
  try {
    if (!import.meta.env.VITE_OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured');
    }

    const response = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [
        {
          role: "system",
          content: `You are a ${language} language expert. Generate plausible but incorrect conjugation options for multiple choice questions.`
        },
        {
          role: "user",
          content: `For the ${language} verb "${infinitive}" in ${tense} tense, the correct answer is "${correctAnswer}".
          
          Generate 3 plausible but incorrect conjugation options that could confuse students. Make them realistic mistakes that students might make.
          
          Return ONLY a JSON object with the options:
          {
            "options": ["option1", "option2", "option3"]
          }`
        }
      ],
      temperature: 0.5,
      max_tokens: 200,
      response_format: { type: "json_object" }
    });

    if (!response.choices[0].message.content) {
      throw new Error('No options received from OpenAI');
    }

    const result = JSON.parse(response.choices[0].message.content);
    const options = Array.isArray(result) ? result : result.options || [];
    
    // Combine correct answer with wrong options and shuffle
    const allOptions = [correctAnswer, ...options.slice(0, 3)];
    return shuffleArray(allOptions);
  } catch (error: any) {
    console.error('Error generating conjugation options:', error);
    
    // Fallback to basic options if AI fails
    return generateFallbackOptions(infinitive, correctAnswer);
  }
};

/**
 * Explain a verb conjugation using OpenAI
 */
export const explainConjugation = async (
  infinitive: string,
  conjugation: string,
  subject: string,
  tense: string,
  language: string = 'spanish'
): Promise<string> => {
  try {
    if (!import.meta.env.VITE_OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured');
    }

    const response = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [
        {
          role: "system",
          content: `You are a helpful ${language} language teacher. Explain verb conjugations in a clear, educational way.`
        },
        {
          role: "user",
          content: `Explain why "${infinitive}" conjugates to "${conjugation}" when used with "${subject}" in ${tense} tense in ${language}. 
          
          Keep the explanation concise (1-2 sentences) and educational. Focus on the conjugation pattern or rule.`
        }
      ],
      temperature: 0.3,
      max_tokens: 150
    });

    if (!response.choices[0].message.content) {
      throw new Error('No explanation received from OpenAI');
    }

    return response.choices[0].message.content.trim();
  } catch (error: any) {
    console.error('Error generating explanation:', error);
    
    // Fallback explanation
    return `"${infinitive}" conjugates to "${conjugation}" with "${subject}" in ${tense} tense following standard ${language} conjugation patterns.`;
  }
};

/**
 * Helper function to select verbs for exercises with balanced difficulty
 */
function selectVerbsForExercises(verbs: any[], numberOfExercises: number): any[] {
  if (verbs.length <= numberOfExercises) {
    return verbs;
  }

  // Group by difficulty
  const easy = verbs.filter(v => v.difficulty === 'easy');
  const medium = verbs.filter(v => v.difficulty === 'medium');
  const hard = verbs.filter(v => v.difficulty === 'hard');

  // Aim for balanced distribution
  const easyCount = Math.ceil(numberOfExercises * 0.4);
  const mediumCount = Math.ceil(numberOfExercises * 0.4);
  const hardCount = numberOfExercises - easyCount - mediumCount;

  const selected = [
    ...easy.slice(0, easyCount),
    ...medium.slice(0, mediumCount),
    ...hard.slice(0, hardCount)
  ];

  // Fill remaining slots if needed
  while (selected.length < numberOfExercises && verbs.length > selected.length) {
    const remaining = verbs.filter(v => !selected.includes(v));
    if (remaining.length > 0) {
      selected.push(remaining[0]);
    } else {
      break;
    }
  }

  return selected.slice(0, numberOfExercises);
}

/**
 * Helper function to shuffle array
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Fallback function to generate basic conjugation options
 */
function generateFallbackOptions(infinitive: string, correctAnswer: string): string[] {
  const root = infinitive.slice(0, -2);
  const ending = infinitive.slice(-2);
  
  let wrongOptions: string[] = [];
  
  if (ending === 'ar') {
    wrongOptions = [
      root + 'o',
      root + 'as', 
      root + 'amos',
      root + 'an'
    ];
  } else if (ending === 'er') {
    wrongOptions = [
      root + 'o',
      root + 'es',
      root + 'emos', 
      root + 'en'
    ];
  } else if (ending === 'ir') {
    wrongOptions = [
      root + 'o',
      root + 'es',
      root + 'imos',
      root + 'en'
    ];
  } else {
    wrongOptions = [
      infinitive + 'a',
      infinitive + 'e',
      infinitive + 's'
    ];
  }

  // Remove correct answer and take 3 wrong options
  wrongOptions = wrongOptions.filter(opt => opt !== correctAnswer).slice(0, 3);
  
  // Combine and shuffle
  return shuffleArray([correctAnswer, ...wrongOptions]);
}

/**
 * Validate if OpenAI service is available
 */
export const isVerbServiceAvailable = (): boolean => {
  return !!import.meta.env.VITE_OPENAI_API_KEY;
};

/**
 * Get service status information
 */
export const getVerbServiceStatus = () => {
  return {
    available: isVerbServiceAvailable(),
    apiKey: !!import.meta.env.VITE_OPENAI_API_KEY,
    model: 'gpt-4-turbo-preview'
  };
};